import { FC, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import { isEmpty, omit } from 'lodash';
import queryString from 'query-string';
import { Button, createBemBlockBuilder } from '@ebsco-ui/ebsco-ui';

import {
  ComponentSpinner,
  PageErrorMessage,
  Pagination,
  SearchOption,
} from '@app/components';
import { useSearchContext } from '@app/contexts';
import { generateRouteParams } from '@app/utils';
import { sharedMessages } from '@app/translations';
import {
  advancedSearchError,
  defaultRecordsPerPage,
  Route,
  URLQuery,
} from '@app/constants';

import { SearchResultsControls } from '../SearchResultsControls';
import { PassthroughLink } from '../PassthroughLink';
import { PageRecords } from './PageRecords';

const cnBem = createBemBlockBuilder(['searchResultsPage']);

export const SearchResults: FC = () => {
  const { $t } = useIntl();
  const {
    state: {
      pageNumber,
      recordsPerPage = defaultRecordsPerPage,
      facets,
      query,
    },
    searchOption,
    shouldRefocusRef,
    activePublicationYear,
    searchResults: {
      searchResultsQuery: {
        isFetching,
        isRefetching,
        isError,
        error,
        fetchStatus,
        status,
        refetch,
      },
      pages,
      currentPage,
      totalRecords = 0,
    },
    updateState,
  } = useSearchContext();
  const [isNewPageLoading, setIsNewPageLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const isSearchPerformed = fetchStatus !== 'fetching' && status !== 'loading';
  const isSearchSuccessful =
    !isRefetching && status === 'success' && !isEmpty(currentPage);
  const shouldRenderResults =
    !isEmpty(pages) && !isError && !isRefetching && totalRecords > 0;

  const handleClearButtonClick = () => {
    const pathsToOmit = !query
      ? [
          searchOption === SearchOption.keyword ? 'option' : '',
          'pageNumber',
          'recordsPerPage',
        ]
      : [];
    const updatedQuery = omit(queryString.parse(location.search), [
      'facets',
      'publicationYear',
      ...pathsToOmit,
    ]);

    updateState({ isInnerUpdate: true });
    navigate(
      generateRouteParams({
        pathname: Route.search,
        query: updatedQuery as URLQuery,
      })
    );
  };

  const renderPageStatus = () => {
    const areFiltersSelected =
      !isEmpty(facets) || !isEmpty(activePublicationYear);

    const isNewSearchRequestLoading = isFetching && !isNewPageLoading;
    const isResultsStateUnsynced = totalRecords > 0 && isEmpty(pages);

    if ((isNewSearchRequestLoading || isResultsStateUnsynced) && !isError) {
      return <ComponentSpinner />;
    }

    if (isEmpty(currentPage) && isError) {
      const isAdvancedSearchError =
        error &&
        error.errors?.[0].code === advancedSearchError.code &&
        error.errors?.[0].type === advancedSearchError.type;

      if (isAdvancedSearchError) {
        return (
          <>
            <PassthroughLink />
            <PageErrorMessage
              title={$t({
                id: 'resultsList.searchInvalidTitle',
                defaultMessage: 'Invalid search',
              })}
              text={$t(sharedMessages.searchInvalidText)}
              buttonText=""
              titleTag="h1"
            />
          </>
        );
      }

      return (
        <>
          <PassthroughLink />
          <PageErrorMessage
            title={$t({
              id: 'resultsList.searchUnavailableTitle',
              defaultMessage: 'Search unavailable',
            })}
            titleTag="h1"
            onClick={refetch}
          />
        </>
      );
    }

    const areResultsEmpty = isSearchPerformed && totalRecords === 0;

    if (areResultsEmpty) {
      return (
        <div data-auto="no-search-results" className={cnBem('__no-results')}>
          <div className={cnBem('__no-results-clue')}>
            {areFiltersSelected ? (
              <>
                <FormattedMessage
                  id="resultsList.noResults.title.message.withFilters"
                  defaultMessage="No results for - {query} - with selected filters."
                  values={{ query }}
                  tagName="h4"
                />
                <div className={cnBem('__no-results-clue-message')}>
                  {$t({
                    id: 'resultsList.noResults.checkSpellingOrModifyFilters',
                    defaultMessage:
                      'Check the spelling, or try a more general term or modify the filters.',
                  })}
                </div>
                <div className={cnBem('__no-results-clue-message')}>
                  {$t({
                    id: 'resultsList.noResults.clearFiltersAndTryAgain',
                    defaultMessage: "Let's clear your filters and try again.",
                  })}
                </div>
                <div className={cnBem('__no-results-clear-button')}>
                  <Button onClick={handleClearButtonClick}>
                    {$t({
                      id: 'resultsList.noResults.clearAllFilters',
                      defaultMessage: 'Clear all filters',
                    })}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <FormattedMessage
                  {...sharedMessages.noResultsForTerm}
                  values={{ term: query }}
                  tagName="h4"
                />
                <div className={cnBem('__no-results-clue-message')}>
                  {$t({
                    id: 'resultsList.noResults.checkSpellingOrTryGeneralTerm',
                    defaultMessage:
                      'Check the spelling, or try a more general term.',
                  })}
                </div>
              </>
            )}
            <div className={cnBem('__no-results-passthrough')}>
              <PassthroughLink />
            </div>
          </div>
        </div>
      );
    }

    if (!isSearchPerformed && isEmpty(pages)) {
      return (
        <h1 className={cnBem('__messageWrapper')}>
          <FormattedMessage
            id="resultsList.enterQuery"
            defaultMessage="Choose a filter or enter a search query to show results."
          />
        </h1>
      );
    }

    return null;
  };

  useEffect(() => {
    if (!isFetching) {
      setIsNewPageLoading(false);
    }
  }, [setIsNewPageLoading, isFetching]);

  return (
    <>
      {renderPageStatus()}
      {shouldRenderResults && (
        <>
          <SearchResultsControls />
          <PassthroughLink />
          {isNewPageLoading && isEmpty(currentPage) && <ComponentSpinner />}
          <PageRecords isSearchSuccessful={isSearchSuccessful} />
          <Pagination
            pageNumber={pageNumber}
            totalRecords={totalRecords}
            recordsPerPage={recordsPerPage}
            onPageChange={updatePageNumber => {
              setIsNewPageLoading(true);

              updateState({
                pageNumber: updatePageNumber,
                isNewPageRequest: true,
              });
              shouldRefocusRef.current = true;
            }}
          />
        </>
      )}
    </>
  );
};
